/**
 * Lead Lifecycle State Machine
 * 
 * Tracks every lead explicitly through a deterministic lifecycle:
 * NEW → ROUTING_IN_PROGRESS → ROUTED | UNSOLD → RETRY_QUEUED | STOCKED → ARCHIVED
 * ROUTING_IN_PROGRESS → RETRY_QUEUED (timeout recovery for stuck leads)
 * 
 * Rules:
 * - A lead can only be in one state at a time
 * - Transitions must be explicit and validated
 * - All transitions update lastUpdatedAt
 * - Invalid transitions throw errors
 */

// --- Lead Status Enum ---

export type LeadStatus =
  | "NEW"
  | "ROUTING_IN_PROGRESS"
  | "ROUTED"
  | "UNSOLD"
  | "RETRY_QUEUED"
  | "STOCKED"
  | "ARCHIVED"
  | "REVIEW_REQUIRED" // Soft validation failure - needs human review
  | "CREDITED"; // Admin credited back — invalid/bad lead

// --- Transition Rules ---

/**
 * Valid state transitions map.
 * Key: current state
 * Value: array of valid next states
 */
const VALID_TRANSITIONS: Record<LeadStatus, LeadStatus[]> = {
  NEW: ["ROUTING_IN_PROGRESS"],
  ROUTING_IN_PROGRESS: ["ROUTED", "UNSOLD", "RETRY_QUEUED"], // RETRY_QUEUED for timeout recovery of stuck leads
  ROUTED: ["ARCHIVED", "CREDITED"],
  UNSOLD: ["RETRY_QUEUED", "STOCKED", "ARCHIVED"],
  RETRY_QUEUED: ["ROUTING_IN_PROGRESS", "STOCKED", "ARCHIVED"],
  STOCKED: ["ARCHIVED"],
  ARCHIVED: [], // Terminal state - no further transitions
  REVIEW_REQUIRED: ["NEW", "ARCHIVED"], // Can be approved (→NEW) or discarded (→ARCHIVED)
  CREDITED: [], // Terminal state - admin credited back
};

// --- Lead Model Extension ---

export interface LifecycleLead {
  // Core identification
  id: string;
  leadTypeId: string;
  leadTypeName: string;
  vertical: 'life_insurance' | 'mortgage';
  state: string;
  estimatedValue: number;
  
  // Lifecycle fields
  status: LeadStatus;
  retryCount: number;
  maxRetries: number;
  nextRetryAt?: number; // Unix timestamp (ms)
  
  // Pricing snapshot (captured when routing starts)
  originalPrice?: number;
  currentPrice?: number;
  pricingSnapshot?: {
    adminMinPrice?: number;
    bidWeight: number;
    qualityWeight: number;
  };
  
  // Routing context (for deterministic DEV override behavior)
  routingContext?: RoutingContextSnapshot;

  // Raw payload + normalized profile for survey data
  leadPayload?: Record<string, unknown>;
  leadProfile?: Record<string, unknown>;

  // Assignment tracking
  assignedAgentId?: string;
  assignedAgentName?: string;
  chargedAmount?: number;
  contractId?: string;
  
  // Validation errors (for REVIEW_REQUIRED status)
  validationErrors?: ValidationErrorEntry[];
  
  // Timestamps
  createdAt: number;
  lastUpdatedAt: number;
}

// Persisted routing context for deterministic DEV behavior
export interface RoutingContextSnapshot {
  devOverridesEnabled: boolean;
  forceAllAgentsEligible?: boolean;
  forceWalletBalance?: number;
  forceLicenses?: string[];
  // Per-agent overrides
  perAgentOverrides?: {
    enabled: boolean;
    agentOverrides: Record<string, {
      forceAcceptAllLeadTypes: boolean;
      overrideAcceptedLeadTypes: string[];
      overrideLicensedStates: string[];
      ignoreContractCaps: boolean;
      ignorePricingRules: boolean;
    }>;
    capturedAt: number;
  };
  // DEV eligibility injection metadata
  eligibilitySource?: 'REAL' | 'DEV_INJECTED';
  devInjectedAgentIds?: string[];
  capturedAt: number;
  source: 'lab' | 'retry' | 'review';
}

// Validation error entry for soft failures
export interface ValidationErrorEntry {
  field: string;
  message: string;
  code: string;
  severity: 'HARD' | 'SOFT';
}

// --- Default Values ---

export const DEFAULT_MAX_RETRIES = 5;

// Exponential backoff delays: 5m, 15m, 45m, 2h, 6h
export const RETRY_DELAYS_MS = [
  5 * 60 * 1000,       // retry 1: 5 min
  15 * 60 * 1000,      // retry 2: 15 min
  45 * 60 * 1000,      // retry 3: 45 min
  2 * 60 * 60 * 1000,  // retry 4: 2 hours
  6 * 60 * 60 * 1000,  // retry 5: 6 hours
];

export const getRetryDelay = (retryCount: number) =>
  RETRY_DELAYS_MS[Math.min(retryCount, RETRY_DELAYS_MS.length - 1)];

/** @deprecated Use getRetryDelay(retryCount) for exponential backoff */
export const DEFAULT_RETRY_DELAY_MS = 5 * 60 * 1000; // kept for backwards compat

// --- State Machine Functions ---

/**
 * Check if a transition is valid
 */
export function canTransition(from: LeadStatus, to: LeadStatus): boolean {
  const validTargets = VALID_TRANSITIONS[from];
  return validTargets.includes(to);
}

/**
 * Get all valid next states for a given status
 */
export function getValidNextStates(status: LeadStatus): LeadStatus[] {
  return [...VALID_TRANSITIONS[status]];
}

/**
 * Transition a lead to a new state with validation
 * Returns a new lead object (immutable pattern)
 * Throws an error if the transition is invalid
 */
export function transitionLead(
  lead: LifecycleLead,
  nextState: LeadStatus,
  updates: Partial<Omit<LifecycleLead, 'id' | 'status' | 'createdAt' | 'lastUpdatedAt'>> = {}
): LifecycleLead {
  if (!canTransition(lead.status, nextState)) {
    throw new Error(
      `Invalid transition: ${lead.status} → ${nextState} for lead ${lead.id}. ` +
      `Valid targets: [${VALID_TRANSITIONS[lead.status].join(', ')}]`
    );
  }

  const now = Date.now();

  // Log transition in dev mode
  if (import.meta.env.MODE !== "production") {
    console.log(`[LeadStateMachine] ${lead.id}: ${lead.status} → ${nextState}`);
  }

  return {
    ...lead,
    ...updates,
    status: nextState,
    lastUpdatedAt: now,
  };
}

/**
 * Get the next recommended action for a lead based on its current state
 */
export function getNextAction(lead: LifecycleLead): {
  action: 'route' | 'retry' | 'stock' | 'archive' | 'wait' | 'none';
  reason: string;
  canExecute: boolean;
  nextRetryIn?: number; // ms until retry is available
} {
  const now = Date.now();

  switch (lead.status) {
    case "NEW":
      return {
        action: 'route',
        reason: 'Lead is new and ready for routing',
        canExecute: true,
      };

    case "ROUTING_IN_PROGRESS":
      return {
        action: 'wait',
        reason: 'Routing is currently in progress',
        canExecute: false,
      };

    case "ROUTED":
      return {
        action: 'archive',
        reason: 'Lead has been successfully routed and can be archived',
        canExecute: true,
      };

    case "UNSOLD":
      if (lead.retryCount < lead.maxRetries) {
        return {
          action: 'retry',
          reason: `Retry available (${lead.retryCount}/${lead.maxRetries} attempts used)`,
          canExecute: true,
        };
      } else {
        return {
          action: 'stock',
          reason: 'All retries exhausted, ready to be stocked',
          canExecute: true,
        };
      }

    case "RETRY_QUEUED":
      if (lead.nextRetryAt && now >= lead.nextRetryAt) {
        return {
          action: 'route',
          reason: 'Retry delay has passed, ready to re-route',
          canExecute: true,
        };
      } else {
        const waitTime = (lead.nextRetryAt ?? now) - now;
        return {
          action: 'wait',
          reason: `Waiting for retry delay (${Math.ceil(waitTime / 1000)}s remaining)`,
          canExecute: false,
          nextRetryIn: waitTime,
        };
      }

    case "STOCKED":
      return {
        action: 'archive',
        reason: 'Lead is stocked and can be archived when no longer needed',
        canExecute: true,
      };

    case "ARCHIVED":
      return {
        action: 'none',
        reason: 'Lead is archived, no further actions allowed',
        canExecute: false,
      };

    case "CREDITED":
      return {
        action: 'none',
        reason: 'Lead was credited back — no further actions allowed',
        canExecute: false,
      };

    default:
      return {
        action: 'none',
        reason: 'Unknown lead status',
        canExecute: false,
      };
  }
}

// --- Factory Functions ---

/**
 * Create a new lifecycle lead from an incoming lead
 */
export function createLifecycleLead(
  incomingLead: {
    id: string;
    leadTypeId: string;
    leadTypeName: string;
    vertical: 'life_insurance' | 'mortgage';
    state: string;
    estimatedValue: number;
  },
  options: {
    maxRetries?: number;
  } = {}
): LifecycleLead {
  const now = Date.now();

  return {
    id: incomingLead.id,
    leadTypeId: incomingLead.leadTypeId,
    leadTypeName: incomingLead.leadTypeName,
    vertical: incomingLead.vertical,
    state: incomingLead.state,
    estimatedValue: incomingLead.estimatedValue,
    status: "NEW",
    retryCount: 0,
    maxRetries: options.maxRetries ?? DEFAULT_MAX_RETRIES,
    createdAt: now,
    lastUpdatedAt: now,
  };
}

// --- Transition Helpers ---

/**
 * Start routing a lead (NEW → ROUTING_IN_PROGRESS)
 */
export function startRouting(
  lead: LifecycleLead,
  pricingSnapshot?: LifecycleLead['pricingSnapshot']
): LifecycleLead {
  return transitionLead(lead, "ROUTING_IN_PROGRESS", {
    pricingSnapshot,
    originalPrice: lead.originalPrice ?? pricingSnapshot?.adminMinPrice,
    currentPrice: pricingSnapshot?.adminMinPrice,
  });
}

/**
 * Mark lead as routed (ROUTING_IN_PROGRESS → ROUTED)
 */
export function markRouted(
  lead: LifecycleLead,
  agentId: string,
  agentName: string,
  chargedAmount: number,
  contractId?: string
): LifecycleLead {
  return transitionLead(lead, "ROUTED", {
    assignedAgentId: agentId,
    assignedAgentName: agentName,
    chargedAmount,
    currentPrice: chargedAmount,
    contractId,
  });
}

/**
 * Mark lead as unsold (ROUTING_IN_PROGRESS → UNSOLD)
 */
export function markUnsold(lead: LifecycleLead): LifecycleLead {
  return transitionLead(lead, "UNSOLD");
}

/**
 * Queue lead for retry (UNSOLD → RETRY_QUEUED)
 */
export function queueRetry(
  lead: LifecycleLead,
  delayMs: number = DEFAULT_RETRY_DELAY_MS
): LifecycleLead {
  if (lead.retryCount >= lead.maxRetries) {
    throw new Error(
      `Cannot queue retry: lead ${lead.id} has exhausted all retries ` +
      `(${lead.retryCount}/${lead.maxRetries})`
    );
  }

  return transitionLead(lead, "RETRY_QUEUED", {
    retryCount: lead.retryCount + 1,
    nextRetryAt: Date.now() + delayMs,
  });
}

/**
 * Start retry routing (RETRY_QUEUED → ROUTING_IN_PROGRESS)
 */
export function startRetryRouting(
  lead: LifecycleLead,
  pricingSnapshot?: LifecycleLead['pricingSnapshot']
): LifecycleLead {
  return transitionLead(lead, "ROUTING_IN_PROGRESS", {
    pricingSnapshot: pricingSnapshot ?? lead.pricingSnapshot,
    nextRetryAt: undefined, // Clear retry timer
  });
}

/**
 * Stock a lead (UNSOLD | RETRY_QUEUED → STOCKED)
 */
export function stockLead(lead: LifecycleLead): LifecycleLead {
  return transitionLead(lead, "STOCKED", {
    currentPrice: lead.originalPrice,
    nextRetryAt: undefined,
  });
}

/**
 * Archive a lead (any terminal-ready state → ARCHIVED)
 */
export function archiveLead(lead: LifecycleLead): LifecycleLead {
  return transitionLead(lead, "ARCHIVED", {
    nextRetryAt: undefined,
  });
}

// --- Status Utilities ---

/**
 * Check if a lead is in a terminal state
 */
export function isTerminal(status: LeadStatus): boolean {
  return status === "ARCHIVED" || status === "CREDITED";
}

/**
 * Check if a lead can still be routed (now or after retry)
 */
export function canBeRouted(lead: LifecycleLead): boolean {
  return (
    lead.status === "NEW" ||
    lead.status === "RETRY_QUEUED" ||
    (lead.status === "UNSOLD" && lead.retryCount < lead.maxRetries)
  );
}

/**
 * Get a human-readable status label
 */
export function getStatusLabel(status: LeadStatus): string {
  const labels: Record<LeadStatus, string> = {
    NEW: "New",
    ROUTING_IN_PROGRESS: "Routing...",
    ROUTED: "Routed",
    UNSOLD: "Unsold",
    RETRY_QUEUED: "Retry Queued",
    STOCKED: "Stocked",
    ARCHIVED: "Archived",
    REVIEW_REQUIRED: "Needs Review",
    CREDITED: "Credited",
  };
  return labels[status];
}

/**
 * Get a badge variant for a status (for UI)
 */
export function getStatusBadgeVariant(
  status: LeadStatus
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "ROUTED":
      return "default";
    case "UNSOLD":
    case "STOCKED":
      return "destructive";
    case "RETRY_QUEUED":
    case "ROUTING_IN_PROGRESS":
      return "secondary";
    case "REVIEW_REQUIRED":
      return "outline"; // Special styling handled separately
    case "CREDITED":
      return "outline"; // Amber styling handled via className
    case "NEW":
    case "ARCHIVED":
    default:
      return "outline";
  }
}

/**
 * Create a lead requiring review (for soft validation failures)
 */
export function createReviewRequiredLead(
  incomingLead: {
    id: string;
    leadTypeId: string;
    leadTypeName: string;
    vertical: 'life_insurance' | 'mortgage';
    state: string;
    estimatedValue: number;
  },
  validationErrors: ValidationErrorEntry[],
  options: {
    maxRetries?: number;
  } = {}
): LifecycleLead {
  const now = Date.now();

  return {
    id: incomingLead.id,
    leadTypeId: incomingLead.leadTypeId,
    leadTypeName: incomingLead.leadTypeName,
    vertical: incomingLead.vertical,
    state: incomingLead.state,
    estimatedValue: incomingLead.estimatedValue,
    status: "REVIEW_REQUIRED",
    retryCount: 0,
    maxRetries: options.maxRetries ?? DEFAULT_MAX_RETRIES,
    validationErrors,
    createdAt: now,
    lastUpdatedAt: now,
  };
}
